import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { makeStyles, Paper } from "@material-ui/core";
import { KButton, KSelect } from "@kyburz/kdesign-system";
import { getAutosuggestData } from "../../../apiCalls/autosuggest";
import DamagesView from "../../../components/damages/DamagesView";
import useSafeSetState from "../../../hooks/useSafeSetState";
import { getVehicleTypes } from "../../../apiCalls/admin/vehicles";
import { Incident } from "../../../interfaces/IncidentInterface";
import { RootStateInterface } from "../../../interfaces/ReduxInterface";

const useStyles = makeStyles((theme) => ({
  header: {
    fontSize: 20,
    fontWeight: 500,
    color: theme.palette.common.black,
    width: "100%",
    paddingBottom: 16,
    borderBottom: "1px solid #e8e8e8",
  },
  paper: {
    padding: 32,
    boxShadow: "none",
    height: "calc(100% - 92px)",
  },
  buttons: {
    width: "100%",
    height: 72,
    display: "flex",
    justifyContent: "flex-end",
    position: "fixed",
    padding: "16px 48px 0 0",
    bottom: 0,
    right: 0,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    background: "white",
  },
  save: {
    marginLeft: 16,
  },
}));

interface Props {
  initialIncident?: Incident;
  handleCancel: Function;
  handleSave: Function;
  viewOnly?: boolean;
}

const VehicleIncident = ({
  initialIncident,
  handleCancel,
  handleSave,
  viewOnly = false,
}: Props) => {
  const classes = useStyles();
  const currentCustomer = useSelector(
    (state: RootStateInterface) => state.customer.currentCustomer,
  );

  const [{ incident, vehicleTypes, vehicles }, setState] = useSafeSetState({
    incident: initialIncident,
    vehicleTypes: {},
    vehicles: [],
  });

  useEffect(() => {
    const customerId = currentCustomer?.shortname;
    getAutosuggestData(customerId, "vehicles").then((data) => {
      const vehiclesResponse = data?.data || [];
      const vehiclesData = vehiclesResponse.map((x) => ({
        name: x?.name,
        label: x?.name,
        id: x?._id,
        type: x?.vehicle_type?.name,
      }));
      setState({ vehicles: vehiclesData });
    });
    getVehicleTypes(customerId).then((data) =>
      setState({ vehicleTypes: data?.data?.data || {} }),
    );
  }, [currentCustomer]);

  useEffect(() => {
    setState({ incident: initialIncident });
  }, [initialIncident]);

  const getVehicleName = () => {
    const vehicleId = incident.vehicle_id;
    return vehicles.find((v) => v.id === vehicleId)?.label;
  };

  const selectVehicle = (data) => {
    const vehicleType = vehicleTypes.find((vt) => vt.name === data.type) || {};
    setState({
      incident: {
        ...incident,
        vehicle_id: data.id,
        vehicle_type: vehicleType,
      },
    });
  };

  const setDamages = (damages) =>
    setState({
      incident: {
        ...incident,
        damages,
      },
    });

  const saveIncident = () =>
    handleSave({
      ...incident,
      damages: incident.damages.map((d) => ({
        ...d,
        photos: d.photos.map((p) => p.url || p),
      })),
    });

  return (
    <Paper className={classes.paper}>
      <div className={classes.header}>Incident details</div>
      <KSelect
        label="Vehicle"
        value={getVehicleName()}
        onSelect={selectVehicle}
        elements={vehicles}
      />
      {incident.vehicle_id && (
        <DamagesView incident={incident} setDamages={setDamages} />
      )}
      <div className={classes.buttons}>
        <KButton children="Cancel" theme="secondary" onClick={() => handleCancel()} />
        <div className={classes.save}>
          <KButton
            children="Save"
            onClick={saveIncident}
            disabled={!incident.vehicle_id || viewOnly}
          />
        </div>
      </div>
    </Paper>
  );
};

export default VehicleIncident;
